import { Button } from "@9amhealth/shared";
import { useBlocNext } from "@blac/react";
import type { FC } from "react";
import React from "react";
import { IconArrows, IconLocker, IconShield } from "src/constants/icons";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import { getHeadingProps, useFocusHeading } from "src/lib/useFocusHeading";
import { toast } from "src/state/state";
import UserPreferencesCubit, {
  UserPreferenceKeys
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import SignupCustomBloc from "./state/SignupCustomBloc";
import { healthInformationExchangeStepStyled } from "./HealthInformationExchangeStep.styled";

const { Header, Title, ImageHeader, Content, ContentInner, Spacer } =
  healthInformationExchangeStepStyled;

const HealthInformationExchangeStep: FC = () => {
  const [, { nextStep, showSaving, hideSaving }] =
    useBlocNext(SignupCustomBloc);
  const [, { updateUserPreferences }] = useBlocNext(UserPreferencesCubit);

  const { headingRef } = useFocusHeading();

  const handleContinue = React.useCallback(async () => {
    showSaving({
      text: translate("savingIndicator.info", { context: "healthInfoExchange" })
    });
    try {
      await updateUserPreferences({
        [UserPreferenceKeys.healthInformationExchangeCompleted]: true
      });
      void nextStep();
    } catch (error) {
      hideSaving();
      reportErrorSentry(error);
      toast.error("error.generic");
    }
  }, [showSaving, nextStep, hideSaving]);

  return (
    <Content>
      <ContentInner>
        <ImageHeader />
        <Spacer $size="lg" />

        <h1 className="as-h4-large" ref={headingRef} {...getHeadingProps()}>
          {translate("healthInfoExchange.simplifyHealthcare")}
        </h1>

        <Spacer $size="md" />
        <p
          className="m0 color-c"
          style={{
            textAlign: "left",
            fontSize: "1rem",
            fontStyle: "normal",
            fontWeight: 600
          }}
        >
          {translate("healthInfoExchange.description")}
        </p>

        <Spacer $size="lg" />
        <Title>
          <IconArrows />
          <Header className="as-body2">
            {translate("healthInfoExchange.connectingProviders.title")}
          </Header>
        </Title>
        <p className="as-little m0">
          {translate("healthInfoExchange.connectingProviders.description")}
        </p>

        <Spacer $size="md" />
        <Title>
          <IconShield />
          <Header className="as-body2">
            {translate("healthInfoExchange.reducingClinicalRisk.title")}
          </Header>
        </Title>
        <p className="as-little m0">
          {translate("healthInfoExchange.reducingClinicalRisk.description")}
        </p>

        <Spacer $size="md" />
        <Title>
          <IconLocker />
          <Header className="as-body2">
            {translate("healthInfoExchange.limitedSecureAccess.title")}
          </Header>
        </Title>
        <p className="as-little m0">
          {translate("healthInfoExchange.limitedSecureAccess.description")}
        </p>

        <Spacer $size="md" />
        <Button onPress={() => void handleContinue()} fullWidth={true}>
          {translate("understood")}
        </Button>
        <Spacer $size="xl" />
      </ContentInner>
    </Content>
  );
};

export default HealthInformationExchangeStep;
